import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Field } from '../components/ui/field';
import { Checkbox, Input, Select } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { can, useMe } from '../lib/session';
import type { SegmentRow, SegmentRules } from '../lib/types';

const RFM = ['champions', 'loyal', 'new', 'at_risk', 'lost', 'regular'] as const;
const MARKETS = ['LV', 'LT', 'EE'] as const;

/** Сегменты клиентов: по правилам (пересчёт ночью) и ручные. */
export function SegmentsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SegmentRow | 'new'>();
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api<{ items: SegmentRow[] }>('/segments'),
  });
  const refresh = async (id: string) => {
    await api(`/segments/${id}/refresh`, { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['segments'] });
  };
  return (
    <>
      <PageHeader
        title={t('seg.title')}
        description={t('seg.hint')}
        actions={
          can(me, 'customers.write') ? (
            <Button
              onClick={() => {
                setEditing('new');
              }}
            >
              {t('seg.new')}
            </Button>
          ) : null
        }
      />
      {segments.error ? <Alert variant="destructive">{errorMessage(segments.error)}</Alert> : null}
      <div className="grid gap-2">
        {(segments.data?.items ?? []).map(({ segment, members }) => (
          <div
            key={segment.id}
            className="flex flex-wrap items-center gap-3 rounded-md border p-3 text-sm"
          >
            <span className="font-medium">{segment.name}</span>
            <span className="font-mono text-xs text-muted-foreground">{segment.code}</span>
            <Badge variant="outline">{segment.rules ? t('seg.dynamic') : t('seg.manual')}</Badge>
            <span>{t('seg.members', { count: members })}</span>
            {can(me, 'customers.write') ? (
              <span className="ml-auto flex gap-2">
                {segment.rules ? (
                  <Button size="sm" variant="outline" onClick={() => void refresh(segment.id)}>
                    {t('seg.refresh')}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditing({ segment, members });
                  }}
                >
                  {t('common.edit')}
                </Button>
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {editing ? (
        <SegmentDialog
          row={editing === 'new' ? undefined : editing}
          onClose={() => {
            setEditing(undefined);
          }}
        />
      ) : null}
    </>
  );
}

function SegmentDialog({ row, onClose }: { row?: SegmentRow; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [code, setCode] = useState(row?.segment.code ?? '');
  const [name, setName] = useState(row?.segment.name ?? '');
  const [dynamic, setDynamic] = useState(row ? row.segment.rules !== null : true);
  const [rules, setRules] = useState<SegmentRules>(row?.segment.rules ?? {});
  const [count, setCount] = useState<number>();
  const [error, setError] = useState<string>();
  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
  const toggle = (key: 'markets' | 'rfm', value: string, on: boolean) => {
    const list = rules[key] ?? [];
    setRules({ ...rules, [key]: on ? [...list, value] : list.filter((x) => x !== value) });
  };

  const preview = async () => {
    setError(undefined);
    try {
      setCount(
        (await api<{ count: number }>('/segments/preview', { method: 'POST', body: rules })).count,
      );
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const save = async () => {
    setError(undefined);
    try {
      await api(row ? `/segments/${row.segment.id}` : '/segments', {
        method: row ? 'PUT' : 'POST',
        body: { code, name, rules: dynamic ? rules : null },
      });
      await queryClient.invalidateQueries({ queryKey: ['segments'] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const numberField = (key: keyof SegmentRules, label: string) => (
    <Field label={label}>
      {(id) => (
        <Input
          id={id}
          type="number"
          min={0}
          value={(rules[key] as number | undefined) ?? ''}
          onChange={(e) => {
            setRules({ ...rules, [key]: num(e.target.value) });
          }}
        />
      )}
    </Field>
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={row ? row.segment.name : t('seg.new')}
      className="max-w-xl"
      footer={
        <>
          {dynamic ? (
            <Button variant="outline" onClick={() => void preview()}>
              {t('seg.preview')}
              {count !== undefined ? `: ${String(count)}` : ''}
            </Button>
          ) : null}
          <Button disabled={name.trim().length < 2 || code.length < 2} onClick={() => void save()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <div className="flex gap-2">
          <Field label={t('seg.name')} className="flex-1">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t('seg.code')}>
            {(id) => (
              <Input
                id={id}
                value={code}
                disabled={!!row}
                onChange={(e) => {
                  setCode(e.target.value.toLowerCase());
                }}
                className="font-mono"
              />
            )}
          </Field>
        </div>
        <label className="flex items-center gap-2">
          <Checkbox
            checked={dynamic}
            onChange={(e) => {
              setDynamic(e.target.checked);
            }}
          />
          {t('seg.byRules')}
        </label>
        {dynamic ? (
          <>
            <fieldset className="flex flex-wrap gap-3">
              <legend className="mb-1 font-medium">{t('ord.market')}</legend>
              {MARKETS.map((m) => (
                <label key={m} className="flex items-center gap-1">
                  <Checkbox
                    checked={rules.markets?.includes(m) ?? false}
                    onChange={(e) => {
                      toggle('markets', m, e.target.checked);
                    }}
                  />
                  {m}
                </label>
              ))}
            </fieldset>
            <fieldset className="flex flex-wrap gap-3">
              <legend className="mb-1 font-medium">RFM</legend>
              {RFM.map((r) => (
                <label key={r} className="flex items-center gap-1">
                  <Checkbox
                    checked={rules.rfm?.includes(r) ?? false}
                    onChange={(e) => {
                      toggle('rfm', r, e.target.checked);
                    }}
                  />
                  {t(`seg.rfm.${r}`)}
                </label>
              ))}
            </fieldset>
            <div className="grid grid-cols-2 gap-2">
              {numberField('minOrders', t('seg.minOrders'))}
              {numberField('maxOrders', t('seg.maxOrders'))}
              {numberField('orderedWithinDays', t('seg.orderedWithin'))}
              {numberField('notOrderedForDays', t('seg.notOrderedFor'))}
            </div>
            <Field label={t('seg.consent')}>
              {(id) => (
                <Select
                  id={id}
                  value={rules.marketingConsent === undefined ? '' : String(rules.marketingConsent)}
                  onChange={(e) => {
                    setRules({
                      ...rules,
                      marketingConsent:
                        e.target.value === '' ? undefined : e.target.value === 'true',
                    });
                  }}
                >
                  <option value="">{t('seg.any')}</option>
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </Select>
              )}
            </Field>
          </>
        ) : (
          <p className="text-muted-foreground">{t('seg.manualHint')}</p>
        )}
        {error ? <Alert variant="destructive">{error}</Alert> : null}
      </div>
    </Dialog>
  );
}
